/**
 * LocalStorageProvider —— StorageProvider 的本地实现（docs/09 §6.2）。
 *
 * docs/10 之后本地模式不再存在：编辑器登录墙前置，项目数据只活在服务端。
 * 本实现仅保留：
 *   - assets：编辑器内 modelDB 作为二进制缓存的数据出口（内容寻址缓存），供
 *     独立运行壳 / standalone 导出产物等「不注入云端 fetcher」的场景使用；
 *   - prefs：设备级兜底（正常路径走云端 /me/prefs）。
 * projects / skills 的本地形态已删除——再有任何代码走到这里说明还有旧调用点（回归要抓的 bug）。
 *
 * 纪律（docs/09 §6.2）：three/*、components-lib/*、runtime/*、runtime-runner/* 永不 import 本模块。
 */
import type {
  AssetMeta,
  AssetRef,
  KeyValueStore,
  ProjectBundle,
  ProjectSummary,
  SaveResult,
  SkillHistoryEntry,
  SkillManifest,
  SkillSummary,
  StorageProvider,
  VersionSummary,
} from '@f3b/shared'
import { modelDBGet, modelDBPut } from '../utils/modelDB'

export class LocalStorageProvider implements StorageProvider {
  readonly kind = 'local' as const

  /** 本地项目形态已删除（docs/10）：一律报错，暴露残留调用点 */
  projects = {
    list: async (): Promise<ProjectSummary[]> => [],
    load: async (): Promise<ProjectBundle> => {
      throw new Error('本地项目已移除：项目数据只存在于云端（docs/10）')
    },
    save: async (): Promise<SaveResult> => {
      throw new Error('本地项目已移除：项目数据只存在于云端（docs/10）')
    },
    versions: async (): Promise<VersionSummary[]> => [],
    restore: async (): Promise<void> => undefined,
  }

  assets = {
    put: async (file: File): Promise<AssetRef> => {
      const buf = await file.arrayBuffer()
      // 本地模式（standalone 产物）以库 ID 为键；编辑器内走云端上传 + 内容寻址
      const libraryId = `local-${crypto.randomUUID()}`
      await modelDBPut(libraryId, buf)
      return { sha256: libraryId, name: file.name, contentType: file.type, sizeBytes: buf.byteLength }
    },

    getBinary: async (ref: AssetRef): Promise<ArrayBuffer> => {
      const buf = await modelDBGet(ref.sha256)
      if (!buf) throw new Error(`本地模型不存在：${ref.sha256}`)
      return buf
    },

    list: async (): Promise<AssetMeta[]> => [],
  }

  /** 本地 Skill 形态已删除（docs/10）：返回空，暴露残留调用点 */
  skills = {
    list: async (): Promise<SkillSummary[]> => [],
    get: async (): Promise<SkillManifest> => {
      throw new Error('本地 Skill 已移除：Skill 数据只存在于云端（docs/10）')
    },
    upsert: async (): Promise<void> => undefined,
    history: async (): Promise<SkillHistoryEntry[]> => [],
  }

  prefs: KeyValueStore = {
    get: async <T>(_key: string): Promise<T | undefined> => undefined,
    set: async (): Promise<void> => undefined,
    remove: async (): Promise<void> => undefined,
  }
}
